import { useTranslation as useI18nTranslation } from 'react-i18next';
import { useMemo } from 'react';

import { SUPPORTED_LANGUAGES, type Language } from '../constants';

import { useProjectName } from './useProjectName';

export const useTranslation = (namespace?: string) => {
  const { t: originalT, i18n } = useI18nTranslation(namespace);
  const projectName = useProjectName();

  // Обертка для t, которая автоматически добавляет projectName в интерполяцию
  // Используем type assertion для сохранения оригинальной типизации react-i18next
  // Это безопасно, т.к. мы вызываем оригинальную функцию с теми же параметрами + projectName
  const t = useMemo(() => {
    const wrappedT = (key: string, options?: object) => {
      return originalT(key, {
        ...options,
        projectName,
      });
    };
    // Type assertion: утверждаем что наша функция имеет тот же тип, что и оригинал
    // Это работает потому что мы сохраняем сигнатуру и поведение оригинала
    return wrappedT as typeof originalT;
  }, [originalT, projectName]);

  return {
    t,
    i18n,
    currentLanguage: i18n.language,
    changeLanguage: i18n.changeLanguage,
    projectName,
  };
};

/**
 * Хук для получения списка языков с переводами
 * @param namespace - namespace для переводов (по умолчанию 'header')
 * @returns Массив языков с переведенными названиями
 */
export const useLanguages = (namespace: string = 'header'): Array<Language & { label: string }> => {
  const { t } = useI18nTranslation(namespace);

  return SUPPORTED_LANGUAGES.map((lang) => ({
    ...lang,
    label: t(lang.translationKey),
  }));
};
